import { useState } from "react";
import type { Store } from "@/api/storeService";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { storeEventProps } from "@/lib/posthog";

type Platforms = Store["platforms"];

type LinkDef = {
  key: keyof Platforms;
  labelAr: string;
  buildHref: (val: string) => string;
  icon: React.ReactNode;
  brandColor: string;
};

// ─── Icons (stroked SVG, sized for 36px circles) ─────────────────────────────

const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const TikTokIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3v10.5a4 4 0 1 1-3-3.87" />
    <path d="M15 3a5 5 0 0 0 5 5" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

// ─── Link definitions ─────────────────────────────────────────────────────────

const LINK_DEFS: LinkDef[] = [
  {
    key: "website",
    labelAr: "الموقع الإلكتروني",
    buildHref: (v) => v,
    icon: <GlobeIcon />,
    brandColor: "var(--ys-primary)",
  },
  {
    key: "instagram",
    labelAr: "إنستجرام",
    buildHref: (v) =>
      v.startsWith("http") ? v : `https://instagram.com/${v.replace("@", "")}`,
    icon: <InstagramIcon />,
    brandColor: "#E1306C",
  },
  {
    key: "facebook",
    labelAr: "فيسبوك",
    buildHref: (v) => (v.startsWith("http") ? v : `https://facebook.com/${v}`),
    icon: <FacebookIcon />,
    brandColor: "#1877f2",
  },
  {
    key: "tiktok",
    labelAr: "تيك توك",
    buildHref: (v) =>
      v.startsWith("http") ? v : `https://www.tiktok.com/@${v.replace("@", "")}`,
    icon: <TikTokIcon />,
    brandColor: "#161823",
  },
  {
    key: "whatsapp",
    labelAr: "واتساب",
    buildHref: (v) =>
      v.startsWith("http") ? v : `https://wa.me/${v.replace(/\D/g, "")}`,
    icon: <WhatsAppIcon />,
    brandColor: "#25D366",
  },
];

// ─── Single icon button ───────────────────────────────────────────────────────

function SocialChip({
  def,
  href,
  onClick,
}: {
  def: LinkDef;
  href: string;
  onClick?: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={def.labelAr}
      title={def.labelAr}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `1px solid ${hovered ? def.brandColor : "var(--ys-rule)"}`,
        background: hovered ? "var(--ys-primary-soft)" : "transparent",
        textDecoration: "none",
        transition: "border-color 150ms, background 150ms",
        cursor: "pointer",
      }}
    >
      <span style={{ color: def.brandColor, display: "flex" }}>{def.icon}</span>
    </a>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function StoreSocialLinks({
  platforms,
  store,
}: {
  platforms: Platforms;
  store?: Store;
}) {
  const { trackStore } = usePostHogTracking();
  const chips = LINK_DEFS.filter((d) => {
    const val = platforms[d.key];
    return typeof val === "string" && val.trim().length > 0;
  });

  if (chips.length === 0) return null;

  return (
    <div
      className="store-links-row"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 9,
        marginTop: 15,
      }}
    >
      {chips.map((def) => (
        <SocialChip
          key={def.key}
          def={def}
          href={def.buildHref(platforms[def.key] as string)}
          onClick={() =>
            trackStore.contactClicked({
              platform: def.key,
              surface: "store_header",
              ...storeEventProps(store),
            })
          }
        />
      ))}
    </div>
  );
}
